import { RefObject, useEffect, useRef } from "react";
import { MenuProps } from "@mui/material";

interface IUseNonBlockingMenuParams {
    /** Whether the non-blocking behaviour is active. */
    enabled: boolean;
    /** Whether the menu is currently open. */
    open: boolean;
    /** Ref of the element the menu is anchored to. */
    anchorRef: RefObject<HTMLElement>;
    /** Called to close the menu when a click lands outside of it. */
    onClose: MenuProps["onClose"];
}

/**
 * Makes a MUI `Menu` non-blocking so the rest of the page stays interactive while
 * it is open.
 *
 * `Menu` is built on `Modal`, whose backdrop captures every pointer event (even a
 * transparent one), which leaves the page inert. The caller renders the modal root
 * and backdrop with `pointer-events: none` so clicks pass through to the page, and
 * keeps the returned `paperRef` element interactive. Because a click-through
 * backdrop no longer triggers `onClose`, this hook closes the menu itself when a
 * click lands outside the paper and the anchor.
 *
 * @returns A ref to attach to the menu paper slot.
 */
export const useNonBlockingMenu = ({ enabled, open, anchorRef, onClose }: IUseNonBlockingMenuParams): RefObject<HTMLDivElement> => {
    const paperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!enabled || !open || !onClose) {
            return;
        }

        const handleOutsidePointerDown = (event: MouseEvent) => {
            const target = event.target as Node | null;
            const isInsidePaper = paperRef.current?.contains(target) ?? false;
            const isOnAnchor = anchorRef.current?.contains(target) ?? false;

            if (isInsidePaper) {
                return;
            }

            if (isOnAnchor) {
                event.preventDefault();
                return;
            }

            onClose(event, "backdropClick");
        };

        document.addEventListener("mousedown", handleOutsidePointerDown, true);

        return () => {
            document.removeEventListener("mousedown", handleOutsidePointerDown, true);
        };
    }, [enabled, open, onClose, anchorRef]);

    return paperRef;
};
